import { useLocation } from "@/lib/router-compat";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Cpu, Bot, Globe, Link2, ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const NotFound = () => {
 const location = useLocation();
 const { language } = useLanguage();

 useEffect(() => {
 console.error("404 Error: User attempted to access non-existent route:", location.pathname);
 }, [location.pathname]);

 const isZh = language === "zh";

 const features = isZh
 ? [
 {
 icon: Cpu,
 title: "AI 原生 + 云原生双基因",
 desc: "从底层架构开始就以 AI 为核心设计，非后期改造。真正的云原生架构确保无限扩展和零运维。",
 },
 {
 icon: Bot,
 title: "AI Agent 矩阵",
 desc: "多个专业 AI Agent 无缝协作，处理运价、订舱、单证和关务，覆盖您的整个运营链路。",
 },
 {
 icon: Globe,
 title: "全球化就绪",
 desc: "内置多语言、多币种、多法规支持。从第一天起就能本地化合规地部署全球业务。",
 },
 {
 icon: Link2,
 title: "上下游无缝集成",
 desc: "看-算-写闭环自动化。无需 API 即可对接任何老旧系统。AI Agent 无缝衔接新老系统。",
 },
 ]
 : [
 {
 icon: Cpu,
 title: "AI Native + Cloud Native DNA",
 desc: "AI-first design from the ground up, not retrofitted. True cloud-native architecture ensures infinite scaling and zero maintenance.",
 },
 {
 icon: Bot,
 title: "AI Agent Matrix",
 desc: "Multiple specialized AI agents collaborate seamlessly, handling rates, booking, documentation, and customs across your entire operations.",
 },
 {
 icon: Globe,
 title: "Global Ready",
 desc: "Built-in multi-language, multi-currency, multi-regulation support. Deploy globally with local compliance from day one.",
 },
 {
 icon: Link2,
 title: "Seamless Integration",
 desc: "See-Calculate-Write closed-loop automation. Connect to any legacy system without APIs. Digital workers bridge old and new systems seamlessly.",
 },
 ];

 return (
 <div className="min-h-screen bg-gradient-hero flex flex-col">
 <Navbar />
 <main className="flex-1 flex flex-col justify-center pt-20 pb-10">
 <div className="container mx-auto px-4">
 {/* 404 Hero */}
 <motion.div
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="text-center mb-6"
 >
 <h1 className="text-[4.8rem] md:text-[6rem] font-bold leading-none text-gradient-ai mb-2">
 404
 </h1>
 <p className="text-base md:text-lg text-foreground/80 font-medium mb-2">
 {isZh ? "页面未找到" : "Page Not Found"}
 </p>
 <p className="text-foreground/60 max-w-md mx-auto mb-6 text-sm">
 {isZh
 ? "抱歉，您访问的页面不存在。请返回首页，或了解 CargoNova 的核心能力。"
 : "Sorry, the page you are looking for does not exist. Return home or explore CargoNova's capabilities."}
 </p>
 </motion.div>


 {/* Core Features */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6, delay: 0.3 }}
 className="mb-8"
 >
 <h3 className="text-center text-sm font-semibold text-foreground/90 mb-4">
 {isZh ? "核心功能介绍" : "Core Capabilities"}
 </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
 {features.map((feature, index) => (
 <motion.div
 key={index}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.5, delay: 0.35 + index * 0.1 }}
 className="glass rounded-xl p-3 hover:bg-black/5 transition-colors"
 >
 <div className="w-6 h-6 rounded-lg bg-secondary/20 flex items-center justify-center mb-2">
 <feature.icon className="w-4 h-4 text-secondary" />
 </div>
 <h4 className="text-foreground font-semibold text-xs mb-1">{feature.title}</h4>
 <p className="text-foreground/60 text-[10px] leading-relaxed">{feature.desc}</p>
 </motion.div>
 ))}
 </div>
 </motion.div>

 {/* CTA Buttons */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6, delay: 0.5 }}
 className="flex flex-col sm:flex-row items-center justify-center gap-3"
 >
 <Button asChild variant="hero" size="sm" className="rounded-full px-5">
 <a href="/">
 <ArrowLeft className="w-4 h-4 mr-2" />
 {isZh ? "返回首页" : "Back to Home"}
 </a>
 </Button>
 <Button asChild variant="hero-outline" size="sm" className="rounded-full px-5">
 <a href="https://www.deepinsights.com.cn" target="_blank" rel="noopener noreferrer">
 <ExternalLink className="w-4 h-4 mr-2" />
 {isZh ? "访问 Deep Insights 官网" : "Visit Deep Insights"}
 </a>
 </Button>
 </motion.div>
 </div>
 </main>
 <Footer />
 </div>
 );
};

export default NotFound;
